/**
 * Search-result card display helpers: reconcile inconsistent backend location fields
 * (e.g. city/ZIP from one state, `state` column still MN) using ZIP3 and coordinates.
 */

import type { ProviderSearchResult } from "../types/providerSearch";

/** USPS ZIP+4 → first 5 digits, then first 3 for prefix lookup */
export function extractUsZip3(zipCode: string | undefined | null): string | null {
  if (!zipCode || typeof zipCode !== "string") return null;
  const digits = zipCode.replace(/\D/g, "");
  if (digits.length < 3) return null;
  return digits.slice(0, 3);
}

/**
 * Primary state for a ZIP3 prefix (approximate; sufficient for fixing common
 * cross-state data errors in search cards). Only includes prefixes we map;
 * unknown ZIP3 → undefined (caller keeps DB state).
 */
const ZIP3_PRIMARY_STATE: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  const addRange = (start: number, end: number, st: string) => {
    for (let i = start; i <= end; i++) {
      m[String(i).padStart(3, "0")] = st;
    }
  };
  // New York (downstate + common upstate; 120–149 mostly NY — keep broad for display fix)
  addRange(100, 149, "NY");
  // Minnesota
  addRange(550, 567, "MN");
  // Wisconsin
  addRange(530, 549, "WI");
  // New Jersey (common; overlaps with NY in 070s handled by NY taking 100–149 first — NJ block separate)
  addRange(70, 89, "NJ");
  return m;
})();

export function getZip3PrimaryState(zip3: string | null): string | undefined {
  if (!zip3) return undefined;
  return ZIP3_PRIMARY_STATE[zip3];
}

/**
 * True when ZIP5 equals a leading 5-digit house number on the street line
 * (e.g. ``13003 PACKARD ST NE`` + zip ``13003`` — not a real postal ZIP).
 */
export function isHouseNumberAsZip(
  address: string | undefined | null,
  zipCode: string | undefined | null
): boolean {
  if (!zipCode || typeof zipCode !== "string") return false;
  if (!address || typeof address !== "string") return false;
  const zipDigits = zipCode.replace(/\D/g, "");
  if (zipDigits.length < 5) return false;
  const zip5 = zipDigits.slice(0, 5);
  const m = address.trim().match(/^(\d{5})(?:\s+|,\s*)\S/);
  return Boolean(m && m[1] === zip5);
}

/**
 * Prefer ZIP-derived state when it disagrees with the stored state and we have a mapping.
 * Avoids showing "NEW YORK, MN" when ZIP is clearly NY.
 *
 * When ``address`` is provided and ZIP equals the street house number, keep the
 * stored state (do not remap via ZIP3 — that ZIP is not postal).
 */
export function displayStateForSearchCard(
  state: string | undefined,
  zipCode: string | undefined | null,
  address?: string | null
): string {
  const s = (state || "").trim().toUpperCase();
  if (isHouseNumberAsZip(address, zipCode)) {
    return state || "";
  }
  const z3 = extractUsZip3(zipCode || undefined);
  const fromZip = z3 ? getZip3PrimaryState(z3) : undefined;
  if (fromZip && s && fromZip !== s) {
    return fromZip;
  }
  if (fromZip && !s) {
    return fromZip;
  }
  return state || "";
}

export function haversineMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3958.7613; // Earth radius in miles
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * When API distance disagrees strongly with straight-line distance from the search center,
 * trust coordinates (bad rows often have NYC coords but wrong metadata / stale distance).
 */
export function reconcileDistanceMiles(
  apiDistance: number | undefined,
  searchCenter: { lat: number; lng: number } | null | undefined,
  providerLat: number | undefined,
  providerLng: number | undefined
): number {
  const dApi = apiDistance;
  if (
    !searchCenter ||
    providerLat == null ||
    providerLng == null ||
    !Number.isFinite(providerLat) ||
    !Number.isFinite(providerLng) ||
    !Number.isFinite(searchCenter.lat) ||
    !Number.isFinite(searchCenter.lng)
  ) {
    return typeof dApi === "number" && Number.isFinite(dApi) ? dApi : 0;
  }

  const dCli = haversineMiles(
    searchCenter.lat,
    searchCenter.lng,
    providerLat,
    providerLng
  );

  if (typeof dApi !== "number" || !Number.isFinite(dApi)) {
    return dCli;
  }

  // If API says distance is 0 but client coords show the provider is non-trivially
  // far away, trust the client-side haversine (stale/merged coordinates on the
  // provider row, or a dedup merge that reset distance_miles to 0).
  if (dApi === 0 && dCli > 0.05) {
    return dCli;
  }

  const diff = Math.abs(dCli - dApi);
  // Far away by coords but API says nearby — trust coords
  if (dCli > 40 && dApi < 25 && diff > 25) {
    return dCli;
  }
  // Any very large discrepancy
  if (diff > 50) {
    return dCli;
  }

  return dApi;
}

/** Resolve display distance for a search/compare row from API distance and coordinates. */
export function getDisplayDistanceMiles(
  result: Pick<
    ProviderSearchResult,
    "distance_miles" | "latitude" | "longitude" | "provider"
  >,
  searchCenter: { lat: number; lng: number } | null | undefined
): number {
  const providerLat = result.latitude ?? result.provider?.latitude;
  const providerLng = result.longitude ?? result.provider?.longitude;
  return reconcileDistanceMiles(
    result.distance_miles,
    searchCenter ?? null,
    providerLat,
    providerLng
  );
}

/** Return a copy with `distance_miles` reconciled against the reference point. */
export function enrichProviderSearchResultDistance(
  result: ProviderSearchResult,
  searchCenter: { lat: number; lng: number } | null | undefined
): ProviderSearchResult {
  const distance = getDisplayDistanceMiles(result, searchCenter);
  if (distance === result.distance_miles) {
    return result;
  }
  return { ...result, distance_miles: distance };
}

/** Comparison panel distance label (full). */
export function formatComparisonDistanceLabel(distanceMiles: number): string {
  if (
    typeof distanceMiles !== "number" ||
    !Number.isFinite(distanceMiles) ||
    distanceMiles <= 0
  ) {
    return "Distance unavailable";
  }
  return `${distanceMiles.toFixed(1)} miles`;
}

/** Comparison panel distance label (compact). */
export function formatComparisonDistanceLabelShort(
  distanceMiles: number
): string {
  if (
    typeof distanceMiles !== "number" ||
    !Number.isFinite(distanceMiles) ||
    distanceMiles <= 0
  ) {
    return "Distance unavailable";
  }
  return `${distanceMiles.toFixed(1)} mi`;
}

/**
 * Trust label for distance / coverage mode (Phase 4).
 * Uses ranking features when present on the search result.
 */
export function formatDistanceTrustSuffix(result: {
  features?: Record<string, unknown> | null;
  distance_miles?: number | null;
  coverage_spatial?: boolean | null;
}): string | null {
  const features = result.features || {};
  if (result.coverage_spatial || features.coverage_spatial === true) {
    return "service area";
  }
  if (
    features.distance_is_approximate === true ||
    (typeof features.provider_coordinate_precision === "string" &&
      !["ROOFTOP", "RANGE_INTERPOLATED"].includes(
        String(features.provider_coordinate_precision)
      ))
  ) {
    return "approx";
  }
  return null;
}
